import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const ClientCoreInfo = ({ 
  client, 
  formData, 
  isEditing, 
  loading, 
  onFieldChange 
}) => {
  const [industryTypes, setIndustryTypes] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(true);

  useEffect(() => {
    fetchIndustryTypes();
  }, []);

  const fetchIndustryTypes = async () => {
    try {
      const response = await api.get('/settings/industry-types');
      // Only show active industry types
      const activeTypes = (response.data.industryTypes || []).filter(type => type.is_active);
      setIndustryTypes(activeTypes);
    } catch (error) {
      console.error('Error fetching industry types:', error);
    } finally {
      setLoadingIndustries(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    onFieldChange(name, value);
  };

  // Handle state field with uppercase conversion
  const handleStateChange = (e) => {
    const { name, value } = e.target;
    // Convert to uppercase and limit to 2 characters
    const upperValue = value.toUpperCase().slice(0, 2);
    onFieldChange(name, upperValue);
  };

  // Find the current industry type name for display
  const currentIndustryType = industryTypes.find(type => type.id === client?.industry_type_id);

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Core Account Information</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">Basic company details and account information.</p>
      </div>
      
      <div className="border-t border-gray-200">
        {isEditing ? (
          <div className="divide-y divide-gray-200">
            {/* Status Checkboxes */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <span className="text-sm font-medium text-gray-500">Account Status</span>
              <div className="mt-1 sm:mt-0 sm:col-span-2 space-y-4">
                <div className="flex items-center">
                  <input
                    id="is_active"
                    name="is_active"
                    type="checkbox"
                    checked={formData.is_active || false}
                    onChange={(e) => onFieldChange('is_active', e.target.checked)}
                    disabled={formData.is_archived || loading}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    Active
                  </label>
                  {formData.is_archived && (
                    <span className="ml-2 text-xs text-gray-500">
                      (Cannot be active while archived)
                    </span>
                  )}
                </div>
                <div className="flex items-center">
                  <input
                    id="is_archived"
                    name="is_archived"
                    type="checkbox"
                    checked={formData.is_archived || false}
                    onChange={(e) => {
                      // If archived, also set inactive
                      if (e.target.checked) {
                        onFieldChange('is_archived', true);
                        onFieldChange('is_active', false);
                      } else {
                        onFieldChange('is_archived', false);
                      }
                    }}
                    disabled={loading}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label htmlFor="is_archived" className="ml-2 block text-sm text-gray-900">
                    Archived
                  </label>
                  <span className="ml-2 text-xs text-gray-500">
                    (Archived clients are automatically inactive)
                  </span>
                </div>
              </div>
            </div>

            {/* Company Name */}
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <label htmlFor="name" className="text-sm font-medium text-gray-500">
                Company Name *
              </label>
              <div className="mt-1 sm:mt-0 sm:col-span-2">
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md disabled:opacity-50"
                  placeholder="Enter company name"
                />
              </div>
            </div>

            {/* DBA */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <label htmlFor="dba" className="text-sm font-medium text-gray-500">
                DBA (Doing Business As)
              </label>
              <div className="mt-1 sm:mt-0 sm:col-span-2">
                <input
                  type="text"
                  name="dba"
                  id="dba"
                  value={formData.dba || ''}
                  onChange={handleChange}
                  disabled={loading}
                  className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md disabled:opacity-50"
                  placeholder="Enter DBA name (if different from company name)"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Alternative business name or trade name
                </p>
              </div>
            </div>

            {/* Client Code */}
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <label htmlFor="client_code" className="text-sm font-medium text-gray-500">
                Client Code
              </label>
              <div className="mt-1 sm:mt-0 sm:col-span-2">
                <input
                  type="text"
                  name="client_code"
                  id="client_code"
                  value={formData.client_code || ''}
                  onChange={handleChange}
                  disabled={loading}
                  className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md disabled:opacity-50"
                  placeholder="e.g., ABC001, CLIENT-123"
                  maxLength="50"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Unique identifier for data imports and consolidation (letters, numbers, hyphens, underscores, periods)
                </p>
              </div>
            </div>

            {/* Address Line 1 */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <label htmlFor="address_line1" className="text-sm font-medium text-gray-500">
                Address Line 1
              </label>
              <div className="mt-1 sm:mt-0 sm:col-span-2">
                <input
                  type="text"
                  name="address_line1"
                  id="address_line1"
                  value={formData.address_line1 || ''}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Street address"
                  className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md disabled:opacity-50"
                />
              </div>
            </div>

            {/* Address Line 2 */}
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <label htmlFor="address_line2" className="text-sm font-medium text-gray-500">
                Address Line 2
              </label>
              <div className="mt-1 sm:mt-0 sm:col-span-2">
                <input
                  type="text"
                  name="address_line2"
                  id="address_line2"
                  value={formData.address_line2 || ''}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Apartment, suite, unit, building, floor, etc."
                  className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md disabled:opacity-50"
                />
              </div>
            </div>

            {/* City, State, Zip Row */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <span className="text-sm font-medium text-gray-500">City, State, ZIP</span>
              <div className="mt-1 sm:mt-0 sm:col-span-2">
                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6">
                  {/* City */}
                  <div className="sm:col-span-3">
                    <label htmlFor="city" className="block text-xs font-medium text-gray-700">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      id="city"
                      value={formData.city || ''}
                      onChange={handleChange}
                      disabled={loading}
                      placeholder="City"
                      className="mt-1 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md disabled:opacity-50"
                    />
                  </div>

                  {/* State */}
                  <div className="sm:col-span-1">
                    <label htmlFor="state" className="block text-xs font-medium text-gray-700">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      id="state"
                      value={formData.state || ''}
                      onChange={handleStateChange}
                      disabled={loading}
                      placeholder="MD"
                      maxLength="2"
                      className="mt-1 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md disabled:opacity-50 uppercase"
                    />
                  </div>

                  {/* ZIP Code */}
                  <div className="sm:col-span-2">
                    <label htmlFor="zip_code" className="block text-xs font-medium text-gray-700">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      name="zip_code"
                      id="zip_code"
                      value={formData.zip_code || ''}
                      onChange={handleChange}
                      disabled={loading}
                      placeholder="12345"
                      className="mt-1 block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md disabled:opacity-50"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Website */}
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <label htmlFor="website" className="text-sm font-medium text-gray-500">
                Website
              </label>
              <div className="mt-1 sm:mt-0 sm:col-span-2">
                <input
                  type="url"
                  name="website"
                  id="website"
                  value={formData.website || ''}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="https://www.company.com"
                  className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md disabled:opacity-50"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Company website URL
                </p>
              </div>
            </div>

            {/* Industry Type */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <label htmlFor="industry_type_id" className="text-sm font-medium text-gray-500">
                Industry Type
              </label>
              <div className="mt-1 sm:mt-0 sm:col-span-2">
                <select
                  id="industry_type_id"
                  name="industry_type_id"
                  value={formData.industry_type_id || ''}
                  onChange={(e) => onFieldChange('industry_type_id', e.target.value || null)}
                  disabled={loading || loadingIndustries}
                  className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md disabled:opacity-50"
                >
                  <option value="">Select an industry type...</option>
                  {industryTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {!loadingIndustries && industryTypes.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    No industry types available. Add some in Settings → Industry Types.
                  </p>
                )}
              </div>
            </div>

            {/* Folder Link */}
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <label htmlFor="folderLink" className="text-sm font-medium text-gray-500">
                Folder Link
              </label>
              <div className="mt-1 sm:mt-0 sm:col-span-2">
                <input
                  type="url"
                  name="folderLink"
                  id="folderLink"
                  value={formData.folderLink || ''}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="https://drive.google.com/..."
                  className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md disabled:opacity-50"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Link to client's shared folder or documentation
                </p>
              </div>
            </div>
          </div>
        ) : (
          <dl>
            {/* Account Status - Display */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Account Status</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    client?.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {client?.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {client?.is_archived && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Archived
                    </span>
                  )}
                </div>
              </dd>
            </div>

            {/* Company Name - Display */}
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Company Name</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {client?.name || 'N/A'}
              </dd>
            </div>

            {/* DBA - Display */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">DBA (Doing Business As)</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {client?.dba || 'N/A'}
              </dd>
            </div>

            {/* Client Code - Display */}
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Client Code</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {client?.client_code ? (
                  <div>
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                      {client.client_code}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Used for data imports and consolidation
                    </p>
                  </div>
                ) : (
                  <span className="text-gray-500">No client code assigned</span>
                )}
              </dd>
            </div>

            {/* Address - Display */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Address</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {client?.address_line1 || client?.address_line2 || client?.city || client?.state || client?.zip_code ? (
                  <div className="space-y-1">
                    {client.address_line1 && (
                      <div>{client.address_line1}</div>
                    )}
                    {client.address_line2 && (
                      <div>{client.address_line2}</div>
                    )}
                    {(client.city || client.state || client.zip_code) && (
                      <div>
                        {[client.city, client.state, client.zip_code].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                ) : (
                  'N/A'
                )}
              </dd>
            </div>

            {/* Website - Display */}
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Website</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {client?.website ? (
                  <a 
                    href={client.website} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-indigo-600 hover:text-indigo-900 break-all"
                  >
                    {client.website}
                  </a>
                ) : (
                  'N/A'
                )}
              </dd>
            </div>

            {/* Industry Type - Display */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Industry Type</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {loadingIndustries ? (
                  <span className="text-gray-500">Loading...</span>
                ) : currentIndustryType ? (
                  <div>
                    <span className="font-medium">{currentIndustryType.name}</span>
                    {currentIndustryType.description && (
                      <p className="text-gray-500 text-xs mt-1">{currentIndustryType.description}</p>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500">No industry type selected</span>
                )}
              </dd>
            </div>

            {/* Folder Link - Display */}
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Folder Link</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {client?.folderLink ? (
                  <a 
                    href={client.folderLink} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-indigo-600 hover:text-indigo-900 break-all"
                  >
                    {client.folderLink}
                  </a>
                ) : (
                  'N/A'
                )}
              </dd>
            </div>

            {/* Created At - Display */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Created At</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {client?.created_at ? new Date(client.created_at).toLocaleString() : 'N/A'}
              </dd>
            </div>
          </dl>
        )}
      </div>
    </div>
  );
};

export default ClientCoreInfo; 